import { api } from "@/lib/api"
import { FeeCategory, TransactionGateway } from "@/lib/types"
// import { useFormStore } from "@/stores/use-form-store"
import {
	type CREATE_TRANSACTION_RESPONSE,
	type FEE_GRID_RESPONSE,
	type FormConfig,
	type ORDER_CREATION_RESPONSE,
	type PAYMENT_META_RESPONSE,
} from "@/types/form-types"
import { logger } from "@/utils/logger"
import { useEffect, useState, useRef } from "react"
import { toast } from "sonner"
// import { useShallow } from "zustand/react/shallow"

interface RazorpayOptions {
	key: string
	amount: number
	currency: string
	name: string
	description: string
	order_id: string
	handler: (response: any) => void
	prefill: {
		name: string
		email: string
		contact: string
	}
	modal: {
		ondismiss: () => void
	}
	theme: {
		color: string
		backdrop_color: string
		hide_topbar: boolean
	}
}

interface RazorpayInstance {
	on: (event: string, callback: (response: any) => void) => void
	open: () => void
	close: () => void
}

interface RazorpayWindow extends Window {
	Razorpay: new (options: RazorpayOptions) => RazorpayInstance
}

declare const window: RazorpayWindow

const loadRazorpay = () =>
	new Promise<void>((resolve, reject) => {
		if (document.getElementById("razorpay-sdk")) {
			resolve()
			return
		}
		const script = document.createElement("script")
		script.id = "razorpay-sdk"
		script.src = "https://checkout.razorpay.com/v1/checkout.js"
		script.onload = () => resolve()
		script.onerror = () => reject("Failed to load Razorpay SDK")
		document.body.appendChild(script)
	})

// Payment lifecycle statuses
type OrderStatus =
	| "IDLE"
	| "PENDING"
	| "CREATED"
	| "PAID"
	| "FAILED"
	| "CANCELLED"
	| "PARTIALLY_REFUNDED"
	| "FULLY_REFUNDED"
	| "ADJUSTED"
type TransactionStatus =
	| "INITIATED"
	| "PENDING"
	| "SUCCESS"
	| "FAILED"
	| "CANCELLED"

type PaymentStatus =
	| "ORDER_NOT_CREATED"
	| "ORDER_CREATED_PENDING_PAYMENT"
	| "PAYMENT_IN_PROGRESS"
	| "PAYMENT_SUCCESS"
	| "PAYMENT_FAILED_RETRY_ALLOWED"
	| "PAYMENT_FAILED_REFUND_REQUESTED"
	| "PAYMENT_FAILED_REFUND_PROCESSING"
	| "PAYMENT_FAILED_REFUNDED"
	| "ORDER_CANCELLED"
	| "ORDER_PARTIALLY_REFUNDED"
	| "ORDER_FULLY_REFUNDED"
	| "ORDER_ADJUSTED"
	| "UNKNOWN"
	| "SERVICE_UNAVAILABLE"

export default function useRazorpay(
	tempFunction: () => void,
	showPaymentGatewayRef: React.MutableRefObject<boolean>,
	setShowPaymentGateway: (showPaymentGateway: boolean) => void,
	periodNumber: number,
	mode?: string,
	config?: FormConfig,
	applicationId?: string,
	fee_grid_data?: FEE_GRID_RESPONSE,
	category?: FeeCategory,
	onComplete?: (response: any) => void,
	amount?: number,
) {
	useEffect(() => {
		if (!config) return

		const id = api.interceptors.request.use(req => {
			req.headers["mode"] = mode
			req.headers["X-Batch-ID"] = config.batch_id
			req.headers["X-Partner-ID"] = config.partner_id
			return req
		})

		return () => api.interceptors.request.eject(id)
	}, [config])

	// const { isPreview } = useFormStore(
	// 	useShallow(state => ({
	// 		isPreview: state.isPreview,
	// 	})),
	// )

	const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("UNKNOWN")
	const [paymentInfo, setPaymentInfo] = useState<{
		order_id: string
		status: PaymentStatus
		amount?: number
		transaction_id?: string
		hideDownloadReceipt?: boolean
		paidAt?: string
	}>()
	const [isLoading, setIsLoading] = useState(true)
	const pollingInterval = useRef<NodeJS.Timeout | null>(null)
	const [transactionStatus, setTransactionStatus] =
		useState<TransactionStatus>("INITIATED")
	// Track when user enters the stage (component mount time)
	const stageEntryTimestamp = useRef<number>(Date.now())

	// Convert timestamp to the desired format
	const formatTimestamp = (timestamp: number) => {
		const date = new Date(timestamp)
		const isoString = date.toISOString() // Returns in 'YYYY-MM-DDTHH:mm:ss.sssZ'

		// Extract milliseconds and add some fraction (you can adjust this if needed)
		const milliseconds = date.getMilliseconds()
		const fractionalMilliseconds = Math.random().toFixed(5).slice(2) // Add up to 5 decimal places
		const formatted = isoString.replace(
			/\.(\d{3})Z$/,
			`.${milliseconds}${fractionalMilliseconds}Z`,
		)

		return formatted
	}

	const formattedTimestamp = formatTimestamp(stageEntryTimestamp.current)

	// ✅ Poll until final status (captured/failed/refunded)
	const startProgessPolling = (
		order_id: string,
		transaction_id: string,
		onComplete?: (response: any) => void,
	) => {
		if (pollingInterval.current) {
			clearTimeout(pollingInterval.current)
		}

		const startTime = Date.now()

		const getNextInterval = (elapsedMinutes: number): number => {
			if (elapsedMinutes < 1) return 3000 // 0-1 min: 3 seconds
			if (elapsedMinutes < 3) return 7000 // 1-3 min: 7 seconds
			if (elapsedMinutes < 5) return 10000 // 3-5 min: 10 seconds
			if (elapsedMinutes < 10) return 20000 // 5-10 min: 20 seconds
			return 30000 // 10+ min: 30 seconds
		}

		const checkStatus = async () => {
			const elapsedMs = Date.now() - startTime
			const elapsedMinutes = elapsedMs / 60000

			try {
				const res = await api.get(
					`/orders/${order_id}/transactions/${transaction_id}/status`,
				)
				if (!res.data) {
					// Schedule next check if no data received
					const nextInterval = getNextInterval(elapsedMinutes)
					pollingInterval.current = setTimeout(
						checkStatus,
						nextInterval,
					)
					return
				}

				const status = res.data.status as TransactionStatus
				const paidAt = res.data.paid_at

				if (showPaymentGatewayRef.current == true) return

				// Only stop polling when we get SUCCESS or FAILED status
				if (["SUCCESS", "FAILED"].includes(status)) {
					clearTimeout(pollingInterval.current as NodeJS.Timeout)
					setTransactionStatus(status)

					if (status === "SUCCESS") {
						setPaymentStatus("PAYMENT_SUCCESS" as PaymentStatus)
						setPaymentInfo({
							order_id: order_id,
							status: "PAYMENT_SUCCESS" as PaymentStatus,
							amount: amount,
							transaction_id: transaction_id,
							paidAt: paidAt,
						})
						if (onComplete) {
							onComplete({
								order_id: order_id,
								transaction_id: transaction_id,
								status: status,
								amount: amount,
							})
						}
						await tempFunction()
					} else if (status === "FAILED") {
						setPaymentStatus(
							"PAYMENT_FAILED_RETRY_ALLOWED" as PaymentStatus,
						)
						setPaymentInfo({
							order_id: order_id,
							status: "PAYMENT_FAILED_RETRY_ALLOWED" as PaymentStatus,
							amount: amount,
							transaction_id: transaction_id,
						})
					}

					return
				}

				// For PENDING or any other status, continue polling
				if (status === "INITIATED") {
					setPaymentStatus("PAYMENT_IN_PROGRESS" as PaymentStatus)
				}

				// Schedule next check with progressive interval
				const nextInterval = getNextInterval(elapsedMinutes)
				pollingInterval.current = setTimeout(checkStatus, nextInterval)
			} catch (err) {
				console.error("Error polling payment status:", err)
				// Continue polling even on error
				const nextInterval = getNextInterval(elapsedMinutes)
				pollingInterval.current = setTimeout(checkStatus, nextInterval)
			}
		}

		// Start the first check immediately
		checkStatus()
	}

	const startPolling = (
		order_id: string,
		transaction_id: string,
		maxElapsedMinutes: number,
		onComplete?: (response: any) => void,
	) => {
		if (pollingInterval.current) {
			clearInterval(pollingInterval.current)
		}

		const startTime = Date.now()
		const maxElapsedMs = 60 * 1000 * maxElapsedMinutes // Convert minutes to milliseconds

		pollingInterval.current = setInterval(async () => {
			const elapsedMs = Date.now() - startTime

			// Check if max time exceeded
			if (elapsedMs >= maxElapsedMs) {
				clearInterval(pollingInterval.current as NodeJS.Timeout)
				setPaymentStatus("PAYMENT_IN_PROGRESS")
				setPaymentInfo({
					order_id: order_id,
					status: "PAYMENT_IN_PROGRESS" as PaymentStatus,
					amount: amount,
					transaction_id: transaction_id,
				})
				return
			}

			try {
				const postBody = {
					batch_id: config?.batch_id || "",
					course_id: fee_grid_data?.course_id || "course_id",
					category: category,
					semester: periodNumber, // Need to check
				}

				let res
				try {
					res = await api.post(
						`/applications/${applicationId}/payment-status`,
						postBody,
					)
					if (
						!res.data ||
						(res.status !== 200 && res.status !== 201)
					) {
						setPaymentStatus("SERVICE_UNAVAILABLE")
						return
					}
				} catch (err) {
					console.error(err)
					setPaymentStatus("SERVICE_UNAVAILABLE")
					return
				}

				const status = res.data.status as PaymentStatus

				if (
					[
						"PAYMENT_SUCCESS",
						"PAYMENT_FAILED_RETRY_ALLOWED",
					].includes(status)
				) {
					clearInterval(pollingInterval.current as NodeJS.Timeout)
					// setTransactionStatus()

					if (status === "PAYMENT_SUCCESS") {
						setTransactionStatus("SUCCESS")
						setPaymentStatus(status)
						setPaymentInfo({
							order_id: order_id,
							status: status,
							amount: res.data?.details?.amount,
							transaction_id: transaction_id,
						})
						if (onComplete) {
							onComplete({
								order_id: order_id,
								transaction_id: transaction_id,
								status: status,
								amount: res.data?.details?.amount,
							})
						}
					} else if (status === "PAYMENT_FAILED_RETRY_ALLOWED") {
						setTransactionStatus("FAILED")
						setPaymentStatus("PAYMENT_FAILED_RETRY_ALLOWED")
						setPaymentInfo({
							order_id: order_id,
							status: "PAYMENT_FAILED_RETRY_ALLOWED" as PaymentStatus,
							amount: res.data?.details?.amount,
							transaction_id: transaction_id,
						})
					} else if (status === "PAYMENT_IN_PROGRESS") {
						setPaymentStatus("PAYMENT_IN_PROGRESS")
					}

					return
				}
			} catch (err) {
				console.error("Error polling payment status:", err)
				setPaymentStatus("SERVICE_UNAVAILABLE")
			}
		}, 3000)
	}

	// ✅ Check initial payment status when user revisits
	const checkInitialPaymentStatus = async () => {
		if (
			!applicationId ||
			applicationId == "" ||
			!config?.batch_id ||
			!fee_grid_data?.course_id
		) {
			setIsLoading(false)
			return
		}
		setIsLoading(true)
		try {
			const postBody = {
				batch_id: config?.batch_id || "",
				course_id: fee_grid_data?.course_id || "course_id",
				category: category,
				semester: periodNumber, // Need to check
			}

			let res
			try {
				res = await api.post(
					`/applications/${applicationId}/payment-status`,
					postBody,
				)
				if (!res.data || (res.status !== 200 && res.status !== 201)) {
					setPaymentStatus("SERVICE_UNAVAILABLE")
					return
				}
			} catch (err) {
				console.error(err)
				setPaymentStatus("SERVICE_UNAVAILABLE")
				return
			}

			const order_id = res.data?.details?.order_id
			const status = res.data?.status as PaymentStatus
			const amount = res.data?.details?.amount
			const transaction_id = res.data?.details?.transaction_id
			const show_receipt = res.data?.details?.show_receipt
			const paidAt = res.data?.details?.updated_at

			setPaymentStatus(status)

			if (status === "PAYMENT_SUCCESS") {
				if (onComplete) {
					setPaymentInfo({
						order_id: order_id,
						status: status,
						amount: amount,
						transaction_id: !show_receipt ? "-" : transaction_id,
						hideDownloadReceipt: !show_receipt,
						paidAt: paidAt,
					})
					onComplete({
						order_id: order_id,
						amount: amount,
						transaction_id: !show_receipt ? "-" : transaction_id,
						status: status,
						hideDownloadReceipt: !show_receipt,
					})
				}
				await tempFunction()
			}

			if (status === "PAYMENT_IN_PROGRESS") {
				// If payment is in progress, start polling
				startPolling(order_id, transaction_id, 1, onComplete)
			}
		} catch (err) {
			console.error("Error fetching payment status:", err)
			setPaymentStatus("SERVICE_UNAVAILABLE")
		} finally {
			setIsLoading(false)
		}
	}

	// ✅ Initiate payment
	async function useHandleOrder(
		selectedPaymentPlan?: string,
		fee_grid_data?: FEE_GRID_RESPONSE,
		category?: FeeCategory,
		setShowPaymentGateway?: (value: boolean) => void,
	) {
		try {
			let orderId = paymentInfo?.order_id

			if (!orderId || orderId === "") {
				function getPeriodType(frequency: string) {
					if (frequency === "PER_YEAR") {
						return "YEAR"
					} else if (frequency === "PER_SEMESTER") {
						return "SEMESTER"
					} else {
						return frequency
					}
				}

				const selectedOrderItems = fee_grid_data?.components?.flatMap(
					item => {
						return item.price_details.map(price => {
							return {
								fee_component_id: item.component_id,
								component_dimension_id: item.dimension_id,
								amount: price.final_price,
								currency: item.currency,
								period_type: getPeriodType(item.frequency),
								period_number: price.period_number,
							}
						})
					},
				)
				const discount =
					fee_grid_data?.application_discount?.discount || 0

				const applicationDiscount = {
					application_id:
						fee_grid_data?.application_discount?.application_id,
					updated_by: fee_grid_data?.application_discount?.updated_by,
					amount: discount,
					currency: fee_grid_data?.application_discount?.currency,
					updated_at: fee_grid_data?.application_discount?.updated_at,
				}

				const postBody = {
					application_id: applicationId,
					batch_id: config?.batch_id,
					course_id: fee_grid_data?.course_id || "course_id",
					fee_grid_revision_id:
						fee_grid_data?.components?.[0]?.revision_id ||
						"DUMMY_FEE_GRID_REVISION_ID",
					category: category,
					plan: selectedPaymentPlan,
					semester: periodNumber, //DOUBT: TO be reveiwed
					currency: fee_grid_data?.components?.[0]?.currency || "INR",
					mode: "SELF",
					method: "ONLINE",
					items: selectedOrderItems,
					adjustments: discount > 0 ? [applicationDiscount] : [],
					created_at: formattedTimestamp,
				}

				const { data: orderRes } =
					await api.post<ORDER_CREATION_RESPONSE>("/orders", postBody)
				if (!orderRes.order.order_id) {
					throw new Error("Failed to create order")
				}

				orderId = orderRes.order.order_id
				if (orderRes.payment_required) {
					const DBOrderstatus = orderRes.order.status as OrderStatus
					if (DBOrderstatus === "PENDING") {
						setPaymentStatus("ORDER_CREATED_PENDING_PAYMENT")
					}
					setPaymentInfo({
						order_id: orderId,
						status: "ORDER_CREATED_PENDING_PAYMENT" as PaymentStatus,
					})
					setShowPaymentGateway?.(true)
				} else {
					logger.info("Payment is not required", orderRes, orderId)
					setPaymentStatus("PAYMENT_SUCCESS")
					setPaymentInfo({
						order_id: orderId!,
						status: "PAYMENT_SUCCESS" as PaymentStatus,
						transaction_id: "-",
						amount: amount,
						hideDownloadReceipt: true,
					})
					onComplete?.({
						order_id: orderId!,
						transaction_id: "-",
						amount: amount,
						status: "PAYMENT_SUCCESS" as PaymentStatus,
					})
				}
			}
		} catch (err) {
			console.error("Error creating order:", err)
			setPaymentStatus("SERVICE_UNAVAILABLE")
		}
	}

	async function useHandlePayment(
		totalAmount: number,
		razorpayKey: string,
		onComplete?: (response: any) => void,
		fee_grid_data?: FEE_GRID_RESPONSE,
		gatewayId?: string,
	) {
		try {
			const leadRes = await api.get<PAYMENT_META_RESPONSE>(
				`/universities/${config?.university_id}/batches/${config?.batch_id}/applications/${applicationId}/payment-meta`,
			)
			if (!leadRes.data) {
				throw new Error("Failed to get payment meta")
			}
			const payload = {
				customer_name: leadRes?.data?.full_name,
				customer_email: leadRes?.data?.email,
				customer_phone: leadRes?.data?.phone,
			}
			logger.info(
				"initiating payment gateway from useHandlePayment",
				payload,
			)
			const res = await api.post<CREATE_TRANSACTION_RESPONSE>(
				`/orders/${paymentInfo?.order_id}/transactions/initiate?gateway=${gatewayId || ""}`,
				payload,
			)
			if (res.status !== 200 && res.status !== 201) {
				throw new Error("Failed to initiate payment")
			}

			const transaction_id = res.data?.transaction_id || "" // Transaction id ----------
			// startPolling(
			// 	paymentInfo?.order_id || "",
			// 	transaction_id,
			// 	5,
			// 	onComplete,
			// )
			startProgessPolling(
				paymentInfo?.order_id || "",
				transaction_id,
				onComplete,
			)

			if (res.data?.gateway === TransactionGateway.Razorpay) {
				await loadRazorpay()
				const razorpay_order_id = res.data?.gateway_order_id || "" // Razorpay order id ----------

				const options: RazorpayOptions = {
					key: razorpayKey,
					amount: totalAmount,
					currency: fee_grid_data?.components?.[0]?.currency || "INR",
					name: leadRes?.data?.university_name || "University",
					description: "Payment for course",
					order_id: razorpay_order_id,
					handler: async (response: any) => {
						logger.info("Transaction handler called")
						if (response.razorpay_order_id !== razorpay_order_id) {
							throw new Error("Payment failed")
						}
						const postbody = {
							notes: {
								application_id: applicationId,
								transaction_id: transaction_id,
							},
							razorpay_order_id: response.razorpay_order_id,
							razorpay_signature: response.razorpay_signature,
							razorpay_payment_id: response.razorpay_payment_id,
						}
						const res = await api.post(
							`/orders/${paymentInfo?.order_id}/transactions/${transaction_id}/verify?gateway=${gatewayId}`,
							postbody,
						)
						if (
							!res ||
							(res.status !== 200 && res.status !== 201) ||
							res.data === false
						) {
							setPaymentStatus("SERVICE_UNAVAILABLE") // TODO: Show Our server is down page, try refreshing after sometime
						}
						logger.info("Payment verified")
					},
					modal: {
						ondismiss: function () {
							setShowPaymentGateway(true)
							showPaymentGatewayRef.current = true
							setPaymentStatus("PAYMENT_FAILED_RETRY_ALLOWED")
							// setPaymentInfo({
							// 	order_id: "",
							// 	status: "PAYMENT_CANCELLED" as PaymentStatus,
							// })
							onComplete?.({
								status: "PAYMENT_FAILED_RETRY_ALLOWED",
							})
						},
					},
					prefill: {
						name: leadRes.data?.full_name || "John Doe",
						email: leadRes.data?.email || "WtH7Q@example.com",
						contact: leadRes.data?.phone || "1234567890",
					},
					theme: {
						color: "#F3F4F6",
						backdrop_color: "#F3F4F6",
						hide_topbar: true,
					},
				}

				const rzp = new window.Razorpay(options)
				rzp.open()
			} else if (gatewayId === TransactionGateway.Easebuzz) {
				// alert("Here should be easebuzz payment")
				// const easebuzz_url = res.data?.data?.payment_url

				// if (!easebuzz_url || !easebuzz_url.length || easebuzz_url === "") {
				//   throw new Error("Easebuzz url not found")
				// }
				// window.location.href = easebuzz_url

				// const easebuzzUrl = res.data?.data?.payment_url
				// if (!easebuzzUrl) {
				// 	throw new Error("Easebuzz URL not found in response")
				// }

				// Extract access key from URL (last part after last slash)
				// const urlParts = easebuzzUrl.split("/")
				const accessKey = res?.data?.data?.access_key

				if (!accessKey) {
					throw new Error(
						"Could not extract access key from Easebuzz URL",
					)
				}

				// seperate mode for eazebuzz
				let mode = res.data?.data?.mode
				if (mode === "UAT") {
					mode = "test"
				} else if (mode === "PROD") {
					mode = "prod"
				}

				// Load Easebuzz script
				await new Promise((resolve, reject) => {
					if ((window as any).EasebuzzCheckout) return resolve(true)

					const script = document.createElement("script")
					script.src =
						"https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js"
					script.async = true
					script.onload = () => resolve(true)
					script.onerror = () =>
						reject(new Error("Failed to load Easebuzz script"))
					document.body.appendChild(script)
				})

				// Initialize Easebuzz
				const easebuzzCheckout = new (window as any).EasebuzzCheckout(
					accessKey,
					mode,
				) // or 'prod' for production

				const options = {
					access_key: accessKey,
					onResponse: (response: any) => {
						if (response.status === "userCancelled") {
							toast.error("Your payment is been cancelled")
							setShowPaymentGateway(true)
							showPaymentGatewayRef.current = true
							setPaymentStatus(
								"PAYMENT_FAILED_RETRY_ALLOWED" as PaymentStatus,
							)
							onComplete?.({
								status: "PAYMENT_FAILED_RETRY_ALLOWED",
							})
							return
						}
						if (response.status === "success") {
							setPaymentInfo({
								order_id: response.udf1,
								status: "PAYMENT_SUCCESS" as PaymentStatus,
								amount: amount,
								transaction_id: response.txnid,
							})
							if (onComplete) {
								onComplete({
									status:
										response.status === "success"
											? "PAYMENT_SUCCESS"
											: "PAYMENT_FAILED_RETRY_ALLOWED",
									transaction_id: response.txnid,
									order_id: response.udf1,
									amount: amount,
									gateway_response: response,
								})
							}
						} else {
							setShowPaymentGateway(true)
							showPaymentGatewayRef.current = true
							setPaymentStatus(
								"PAYMENT_FAILED_RETRY_ALLOWED" as PaymentStatus,
							)
							onComplete?.({
								status: "PAYMENT_FAILED_RETRY_ALLOWED",
							})
						}
						// window.location.reload()
					},
					theme: "#123456", // Your theme color
				}

				// Open Easebuzz payment modal
				easebuzzCheckout.initiatePayment(options)
			} else {
				alert("Gateway not supported...")
			}

			// useHandleOrder(selectedPaymentPlan, fee_grid_data, category)
		} catch (err) {
			console.error("Error loading Gateway:", err)
			// alert("Error loading Razorpay")
			throw err
		}
	}

	useEffect(() => {
		checkInitialPaymentStatus()
		return () => {
			if (pollingInterval.current) {
				clearTimeout(pollingInterval.current)
			}
		}
	}, [fee_grid_data])

	return {
		paymentStatus,
		transactionStatus,
		paymentInfo,
		useHandleOrder,
		useHandlePayment,
		checkInitialPaymentStatus,
		isLoading,
	}
}
